import { useState, useEffect, useCallback } from "react";
import { Anchor, LogOut, LayoutGrid, ClipboardList, Building2, Users, Briefcase, MessageSquare, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { adminApi } from "@/lib/adminApi";
import AdminOverview from "@/components/admin/AdminOverview";
import { AdminApplications, AdminEmployerRequests, AdminUsers } from "@/components/admin/AdminTables";
import AdminJobs from "@/components/admin/AdminJobs";
import AdminMessages from "@/components/admin/AdminMessages";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "applications", label: "Applications", icon: ClipboardList },
  { id: "employers", label: "Talent Requests", icon: Building2 },
  { id: "users", label: "Registered Users", icon: Users },
  { id: "jobs", label: "Job Listings", icon: Briefcase },
  { id: "messages", label: "Messages", icon: MessageSquare },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    try { setStats(await adminApi.overview()); } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats, tab]);

  const badge = (id) => {
    if (!stats) return null;
    if (id === "applications" && stats.new_applications > 0) return stats.new_applications;
    if (id === "employers" && stats.open_requests > 0) return stats.open_requests;
    if (id === "messages" && stats.unread_messages > 0) return stats.unread_messages;
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0B0F14] text-[#F4F6F8] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-white/10 bg-[#10161F] hidden md:flex flex-col fixed inset-y-0 left-0 z-40">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/10">
          <span className="h-8 w-8 grid place-items-center bg-[#FF5E1E] text-[#0B0F14]"><Anchor size={18} strokeWidth={2.5} /></span>
          <div className="leading-none">
            <div className="font-display font-black uppercase text-sm">Reentry<span className="text-[#FF5E1E]">.</span>Admin</div>
            <div className="font-mono-tech text-[9px] tracking-[0.2em] text-[#8FA3B8] mt-0.5">CONTROL DECK</div>
          </div>
        </div>
        <nav className="flex-1 py-4">
          {TABS.map((t) => {
            const b = badge(t.id);
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`w-full flex items-center gap-3 px-5 py-3 font-mono-tech text-[11px] tracking-[0.14em] uppercase transition-colors border-l-2 ${
                  active ? "border-[#FF5E1E] bg-[#FF5E1E]/10 text-white" : "border-transparent text-[#8FA3B8] hover:text-white hover:bg-white/5"
                }`}
              >
                <t.icon size={16} className={active ? "text-[#FF5E1E]" : ""} />
                <span className="flex-1 text-left">{t.label}</span>
                {b != null && <span className="min-w-[18px] h-[18px] px-1 grid place-items-center bg-[#FF5E1E] text-[#0B0F14] text-[10px] font-bold">{b}</span>}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="font-mono-tech text-[9px] tracking-[0.15em] text-[#8FA3B8] mb-1">SIGNED IN</div>
          <div className="text-sm font-medium truncate">{user?.name}</div>
          <button data-testid="admin-logout-button" onClick={logout} className="mt-3 w-full flex items-center justify-center gap-2 border border-white/15 py-2 text-[#8FA3B8] hover:text-white hover:border-[#FF5E1E]/60 transition-colors font-mono-tech text-[10px] tracking-[0.14em] uppercase">
            <LogOut size={14} /> Log Out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#10161F] border-b border-white/10">
        <div className="h-14 flex items-center justify-between px-4">
          <div className="flex items-center gap-2"><span className="h-7 w-7 grid place-items-center bg-[#FF5E1E] text-[#0B0F14]"><Anchor size={15} /></span><span className="font-display font-black uppercase text-sm">Admin</span></div>
          <button onClick={logout} className="text-[#8FA3B8]" data-testid="admin-mobile-logout"><LogOut size={16} /></button>
        </div>
        <div className="flex overflow-x-auto border-t border-white/10">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-mtab-${t.id}`} onClick={() => setTab(t.id)} className={`shrink-0 px-4 py-2.5 font-mono-tech text-[9px] tracking-[0.12em] uppercase ${tab === t.id ? "text-[#FF5E1E]" : "text-[#8FA3B8]"}`}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 md:ml-64 pt-28 md:pt-8 px-4 sm:px-6 lg:px-10 pb-16">
        <div className="max-w-6xl">
          {tab === "overview" && <AdminOverview stats={stats} onGo={setTab} onRefresh={loadStats} />}
          {tab === "applications" && <AdminApplications />}
          {tab === "employers" && <AdminEmployerRequests />}
          {tab === "users" && <AdminUsers />}
          {tab === "jobs" && <AdminJobs />}
          {tab === "messages" && <AdminMessages onChange={loadStats} />}
        </div>
      </main>
    </div>
  );
}

export function AdminHeader({ eyebrow, title, sub, right }) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <div className="font-mono-tech text-[10px] tracking-[0.25em] text-[#FF5E1E] mb-2">[ {eyebrow} ]</div>
        <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl">{title}</h1>
        {sub && <p className="mt-2 text-sm text-[#8FA3B8]">{sub}</p>}
      </div>
      {right}
    </div>
  );
}

export { Bell };
